import React, { useState, useEffect, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import { ReduxState, ViewState, LoadingData } from 'services/types';
import {
	OrderInfo,
	ProductApi,
	Shop,
	OrderProduct,
	Shopper,
	Category,
	Languages,
	Location,
	ById,
} from 'common/types';
import ProductSelection from 'components/ProductSelection';
import CategorySelector from 'components/CategorySelector';
import {
	addProduct,
	addNewShopper,
	changeSelectedCategoryId,
	updateProductsForShopper,
	setRentNowToggle,
	deleteShopper,
	deleteProduct,
	updateProductAvailabilities,
	updateRentalInfoCurrency,
	createReservation,
} from 'actions';
import { Theme, Box } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import Container from 'components/Container';
import Spinner from 'components/Spinner';
import Typography from '@mui/material/Typography';
import TimeLocationSelector from 'components/TimeLocationSelector';
import { getUniqueProductsFromVariantIds } from 'common/utils/newRentalUtils';
import {
	getOverbookedVariantIds,
	getProductCurrentAvailabilityCount,
	getSelectedVariantsCount,
	ProductTotalAvailability,
} from 'common/api/frontend/inventory_new';
import { handleQuantityAndAvailabilityFetch } from 'common/api/frontend/inventory_new/getQuantitiesAndAvailabilities';
import { getShopLocationsWithContact } from 'common/utils/shopUtils';
import {
	getLocationStockProducts,
	getStockProductsWithSetProducts,
} from 'common/utils/productVisibilityFilters';
import { useTranslation } from 'services/localization/useTranslation';
import * as UserSelectors from 'selectors/UserSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as NewRentalSelectors from 'selectors/NewRentalSelectors';
import NewRentalSideBar from './components/NewRentalSideBar';
import BottomBar from './components/BottomBar';
import { push } from 'routing';
import {
	getEndDateWithInventoryBlockers,
	getStartDateWithInventoryBlockers,
} from 'common/utils/productUtils';
import { usePermissions } from 'hooks/usePermissions';
import OverbookingDialog from './components/OverbookingDialog';
import { useHasFeature } from 'hooks';

interface DispatchProps {
	addProduct: (product: OrderProduct) => void;
	deleteProduct: (productId: string) => void;
	addNewShopper: (id: string) => void;
	changeSelectedCategoryId: (categoryId: string) => void;
	updateProductsForShopper: (products: OrderProduct[], shopperId: string) => void;
	setRentNowToggle: (value: boolean) => void;
	deleteShopper: (shopperId: string) => void;
	updateProductAvailabilities: (productAvailabilities: ById<ProductTotalAvailability>) => void;
	updateRentalInfoCurrency: (code: string) => void;
	createReservation: () => void;
}

interface StateToProps {
	selectedCategoryId: string;
	categories: LoadingData<Category[]>;
	stockProducts: LoadingData<ProductApi[]>;
	rentalInfo: OrderInfo;
	activeShop: Shop;
	view: ViewState;
	rentalProducts: OrderProduct[];
	shoppers: Shopper[];
	productAvailabilities: ById<ProductTotalAvailability>;
	lang: Languages;
}

type Props = DispatchProps & StateToProps & WithStyles<typeof styles>;

const NewRental = (props: Props) => {
	const { hasFeature } = useHasFeature();
	const [activeShopperId, setActiveShopperId] = useState<string>(props.shoppers[0].id);
	const activeShopper = props.shoppers.find((shopper) => shopper.id === activeShopperId);
	const [loadingProductAvailability, setLoadingProductAvailability] = useState<boolean>(true);
	const [
		availabilityNotLoadedForLongDuration,
		setAvailabilityNotLoadedForLongDuration,
	] = useState<boolean>(false);
	const [overbookingDialogOpen, setOverbookingDialogOpen] = useState<boolean>(false);
	const [routeToContinue, setRouteToContinue] = useState<string | undefined>(undefined);

	const overbookedVariantIds = useSelector(NewRentalSelectors.overbookedVariantIds);

	const { hasPermission } = usePermissions();

	const allowOverbookings = hasPermission('allow_overbookings');

	const prevRentalProducts = usePrevious(props.rentalProducts);

	const overbookedProducts = getUniqueProductsFromVariantIds(
		props.rentalProducts,
		overbookedVariantIds,
	);

	useEffect(() => {
		if (!activeShopper && props.shoppers.length > 0) {
			setActiveShopperId(props.shoppers[0].id);
		}
	}, [activeShopper, props.shoppers]);

	const { t } = useTranslation();

	const activeLocationId = useSelector(UserSelectors.userActiveLocationId);
	const start = useSelector(ViewSelectors.newRentalStartDate);
	const end = useSelector(ViewSelectors.newRentalEndDate);
	const shopId = props.activeShop.shopInfo.id;
	const currency = props.activeShop.publicInfo.currency;
	const shopLocations = getShopLocationsWithContact(props.activeShop.publicInfo, 'ADMIN');

	const {
		stockProducts: allStockProducts,
		categories,
		classes,
		selectedCategoryId,
		activeShop,
		updateRentalInfoCurrency,
	} = props;
	const isMobile = window.matchMedia('screen and (max-width: 700px)').matches;

	const allStockProductsData = allStockProducts.data;
	const stockProductsLoading = allStockProducts.loading;
	const stockProductsError = allStockProducts.error;

	const locationStockProducts =
		!!activeLocationId && allStockProductsData
			? getLocationStockProducts(allStockProductsData, activeLocationId, 'ADMIN')
			: null;

	const locationStockProductsWithSetProducts =
		allStockProductsData && locationStockProducts
			? getStockProductsWithSetProducts(locationStockProducts, allStockProductsData)
			: [];

	useEffect(() => {
		if (props.rentalInfo.pricing.currency !== currency.code) {
			updateRentalInfoCurrency(currency.code);
		}
	}, [currency.code, updateRentalInfoCurrency, props.rentalInfo.pricing.currency]);

	useEffect(() => {
		let mounted = true;
		if (!activeLocationId) return;
		if (mounted && locationStockProductsWithSetProducts && allStockProductsData) {
			if (hasFeature('DISABLE_PRODUCT_AVAILABILITY_LOADING', { scope: 'shop' })) {
				/* TEMPORARY - remove when performance / long loading issue in the new rental
				view has been fixed */
				setAvailabilityNotLoadedForLongDuration(true);
				setLoadingProductAvailability(false);
			} else {
				setAvailabilityNotLoadedForLongDuration(false);
				const productsToFetchAvailabilityFor = locationStockProductsWithSetProducts;
				handleAvailabilityFetch(
					productsToFetchAvailabilityFor,
					allStockProductsData,
					activeLocationId,
					shopLocations,
				);
			}
		}
		return () => {
			mounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allStockProducts, props.view.startDate, props.view.endDate, end, start, activeLocationId]);

	useEffect(() => {
		let mounted = true;
		if (mounted && prevRentalProducts && allStockProductsData) {
			handleUpdateProductAvailability(prevRentalProducts, allStockProductsData);
		}
		return () => {
			mounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.rentalProducts, prevRentalProducts, allStockProductsData]);

	useEffect(() => {
		let mounted = true;
		if (mounted && allStockProductsData && !loadingProductAvailability) {
			handleUpdateProductAvailability([], allStockProductsData);
		}
		return () => {
			mounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadingProductAvailability]);

	const createBookingAndGoToRoute = (route?: string) => {
		props.createReservation();
		if (route) {
			push(route);
			return;
		}
		if (routeToContinue) {
			push(routeToContinue);
			return;
		}
	};

	const handleUpdateProductAvailability = (
		prevRentalProducts: OrderProduct[],
		stockProducts: ProductApi[],
	) => {
		const updatedProductAvailabilities = { ...props.productAvailabilities };
		stockProducts
			.map((p) => p.id)
			.forEach((pId) => {
				const productAvailability = props.productAvailabilities[pId];
				if (!!productAvailability) {
					const product = stockProducts.find((p) => p.id === pId);
					if (!!product) {
						const updatedAvailability = getProductCurrentAvailabilityCount(
							product,
							productAvailability,
							stockProducts,
							getSelectedVariantsCount(props.rentalProducts),
						);
						updatedProductAvailabilities[pId] = {
							...props.productAvailabilities[pId],
							productAvailabilityWithOrderProducts: updatedAvailability,
						};
					}
				}
			});
		props.updateProductAvailabilities(updatedProductAvailabilities);
	};

	const handleAvailabilityFetch = async (
		products: ProductApi[],
		allStockProducts: ProductApi[],
		startLocationId: string,
		shopLocations: Location[],
	) => {
		setLoadingProductAvailability(true);
		const productAvailabilities = await Promise.all(
			products.map((product) => {
				const startDateWithInventoryBlockers = getStartDateWithInventoryBlockers({
					startDate: start,
				});
				const endDateWithInventoryBlockers = getEndDateWithInventoryBlockers(
					end,
					product,
					allStockProducts,
				);
				return handleQuantityAndAvailabilityFetch({
					product,
					stockProducts: allStockProducts,
					startDate: startDateWithInventoryBlockers,
					endDate: endDateWithInventoryBlockers,
					shopId,
					startLocationId,
					shopLocations,
					purchaseType: 'rental',
				});
			}),
		);

		const updatedProductAvailabilities: ById<ProductTotalAvailability> = {
			...props.productAvailabilities,
		};
		productAvailabilities.forEach((productAvailabilityObject) => {
			const product = allStockProducts.find((p) => p.id === productAvailabilityObject.id);

			if (product) {
				const availabilityWithOrderProducts = getProductCurrentAvailabilityCount(
					product,
					productAvailabilityObject,
					allStockProducts,
					getSelectedVariantsCount(props.rentalProducts),
				);
				updatedProductAvailabilities[product.id] = productAvailabilityObject;
				updatedProductAvailabilities[
					product.id
				].productAvailabilityWithOrderProducts = availabilityWithOrderProducts;
			}
		});

		props.updateProductAvailabilities(updatedProductAvailabilities);
		setLoadingProductAvailability(false);
		return updatedProductAvailabilities;
	};

	const handleProductAdd = (product: OrderProduct) => {
		props.addProduct(product);
	};

	const handleProductDelete = (productId: string) => {
		props.deleteProduct(productId);
	};

	const updateShopperAdditionalProducts = (newProducts: OrderProduct[], shopperId: string) => {
		props.updateProductsForShopper(newProducts, shopperId);
	};

	const handleSelectedCategoryIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		props.changeSelectedCategoryId(e.target.value);
	};

	const updatedAvailabilityForSelectedProducts = async () => {
		if (!allStockProductsData || !activeLocationId) {
			return;
		}
		const productsForRental = allStockProductsData.filter((product) =>
			props.rentalProducts.some((p) => p.productApiIds.includes(product.id)),
		);
		const availabilityForSelectedProducts = await handleAvailabilityFetch(
			productsForRental,
			allStockProductsData,
			activeLocationId,
			shopLocations,
		);
		return availabilityForSelectedProducts;
	};

	const clearShoppersWithoutProducts = () => {
		const shopperIdsWithProducts = [...new Set(props.rentalProducts.map((p) => p.shopperId))];
		for (const shopper of props.shoppers) {
			if (!shopperIdsWithProducts.includes(shopper.id)) {
				props.deleteShopper(shopper.id);
			}
		}
	};

	const validateRentalAndGoToRoute = async (route: string) => {
		clearShoppersWithoutProducts();
		if (allowOverbookings && !!overbookedProducts?.length) {
			createBookingAndGoToRoute(route);
			return;
		}
		const updatedAvailabilityForProducts = await updatedAvailabilityForSelectedProducts();
		const updatedOverbookedVariantIds =
			updatedAvailabilityForProducts &&
			getOverbookedVariantIds(updatedAvailabilityForProducts, props.rentalProducts);
		if (allowOverbookings && !overbookedProducts?.length && !!updatedOverbookedVariantIds?.length) {
			setOverbookingDialogOpen(true);
			setRouteToContinue(route);
			return;
		}
		if (updatedOverbookedVariantIds?.length === 0) {
			createBookingAndGoToRoute(route);
		}
	};

	if (!activeShop) {
		return null;
	}

	if (!shopLocations.length) {
		return (
			<Container>
				<Box mt={4} />
				<Typography variant="h5" align="center" gutterBottom>
					{t('newRental.allLocationsAreClosed', 'All the locations are closed')}
				</Typography>
				<Typography variant="body1" align="center">
					{t(
						'newRental.setFromSettings',
						'You can adjust store visibility under Settings > General',
					)}
				</Typography>
			</Container>
		);
	}

	if (!shopLocations.find((l) => l.id === activeLocationId)) {
		return (
			<Container>
				<Box mt={4} />
				<Typography variant="h5" align="center" gutterBottom>
					{t('newRental.storeLocationClosed', 'This store is hidden from Admin')}
				</Typography>
				<Typography variant="body1" align="center">
					{t(
						'newRental.setFromSettings',
						'You can adjust store visibility under Settings > General',
					)}
				</Typography>
			</Container>
		);
	}

	return (
		<div className={classes.root}>
			<div className={classes.contentWrapper}>
				<div className={classes.productView}>
					<TimeLocationSelector />
					<Container className={classes.main}>
						{locationStockProducts && categories.data && (
							<CategorySelector
								categories={categories.data}
								stockProducts={locationStockProducts}
								selectedCategoryId={props.selectedCategoryId}
								lang={props.lang}
								handleSelectedCategoryIdChange={handleSelectedCategoryIdChange}
							/>
						)}
						{locationStockProducts && categories.data && !!activeShopper && !!activeLocationId ? (
							<ProductSelection
								stockProducts={locationStockProductsWithSetProducts}
								rentalProducts={props.rentalProducts}
								categories={categories.data}
								selectedCategoryId={selectedCategoryId}
								activeShopperId={activeShopperId}
								activeShopperSegment={activeShopper.segment}
								lang={props.lang}
								duration={props.view.duration}
								startDate={props.view.startDate}
								endDate={props.view.endDate}
								activeShop={activeShop}
								rentalInfo={props.rentalInfo}
								productAvailabilities={props.productAvailabilities}
								loadingProductAvailability={loadingProductAvailability}
								handleProductAdd={(p: OrderProduct) => handleProductAdd(p)}
								handleProductDelete={(pId: string) => handleProductDelete(pId)}
								updateShopperAdditionalProducts={(newProducts: OrderProduct[], shopperId: string) =>
									updateShopperAdditionalProducts(newProducts, shopperId)
								}
								availabilityNotLoadedForLongDuration={availabilityNotLoadedForLongDuration}
								locationId={activeLocationId}
							/>
						) : stockProductsLoading ? (
							<Spinner in={true} />
						) : (
							<div>{stockProductsError}</div>
						)}
					</Container>
				</div>
				{!isMobile && (
					<NewRentalSideBar
						activeShopperId={activeShopperId}
						setActiveShopperId={(id: string) => setActiveShopperId(id)}
						validateRentalAndGoToRoute={(route: string) => validateRentalAndGoToRoute(route)}
						rentalProducts={props.rentalProducts}
						shoppers={props.shoppers}
						stockProducts={locationStockProductsWithSetProducts}
						overbookedProducts={overbookedProducts}
					/>
				)}
			</div>
			{isMobile && (
				<BottomBar
					rentalInfo={props.rentalInfo}
					rentalProducts={props.rentalProducts}
					activeShopperId={activeShopperId}
					setActiveShopperId={(id: string) => setActiveShopperId(id)}
					shoppers={props.shoppers}
					addNewShopper={(id: string) => addNewShopper(id)}
					validateRentalAndGoToRoute={(route: string) => validateRentalAndGoToRoute(route)}
					overbookedProducts={overbookedProducts}
					stockProducts={locationStockProductsWithSetProducts}
				/>
			)}
			{overbookingDialogOpen && overbookedProducts && (
				<OverbookingDialog
					isOpen={overbookingDialogOpen}
					isMobile={isMobile}
					overbookedProducts={overbookedProducts}
					onClose={() => setOverbookingDialogOpen(false)}
					confirmCallback={createBookingAndGoToRoute}
				/>
			)}
		</div>
	);
};

const mapStateToProps = ({ stock, newRental, shop, view, lang }: ReduxState): StateToProps => {
	return {
		selectedCategoryId: newRental.selectedCategoryId,
		categories: stock.categories,
		stockProducts: stock.stockProducts,
		rentalInfo: newRental.rentalInfo,
		activeShop: shop.shop.data!,
		rentalProducts: newRental.products,
		shoppers: newRental.shoppers,
		view,
		productAvailabilities: newRental.productAvailabilities,
		lang,
	};
};

const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			flexDirection: 'column',
			flex: '1 1 auto',
			overflow: 'hidden',
			height: 'calc(100vh - 56px - 32px)', // Height of header bar and status bar
			[theme.breakpoints.up('sm')]: {
				height: 'calc(100vh - 64px - 32px)', // Height of header bar and status bar
			},
		},
		contentWrapper: {
			display: 'flex',
			flexDirection: 'row',
			flex: '1 1 auto',
			height: '100%',
		},
		productView: {
			flex: '1 1 auto',
			overflow: 'auto',
			'-webkit-overflow-scrolling': 'touch',
			display: 'flex',
			flexDirection: 'column',
			background: theme.palette.background.secondary,
		},
		main: {
			position: 'relative',
			paddingTop: 16,
			paddingBottom: 64 + 16,
			'-webkit-overflow-scrolling': 'touch',
		},
		close: {
			padding: theme.spacing(0.5),
		},
	});

const dispatchProps = {
	addProduct,
	addNewShopper,
	changeSelectedCategoryId,
	updateProductsForShopper,
	setRentNowToggle,
	deleteShopper,
	deleteProduct,
	updateProductAvailabilities,
	updateRentalInfoCurrency,
	createReservation,
};

export default withStyles(styles)(connect(mapStateToProps, dispatchProps)(NewRental));

const usePrevious = <T extends {}>(value: T) => {
	const ref = useRef<T>();

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
};
